import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { TextField, Autocomplete, createFilterOptions, Skeleton } from "@mui/material";
import type { Territoire } from "models";
import { territoiresService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import { stringUtil } from "@sdeapps/react-core";
import { sortingUtil } from "utils";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: (option: Territoire) => {
    const normalizedString = stringUtil.normalize(option.libelle);
    return `${normalizedString} ${option.libelle}`;
  },
});

interface TerritoireAutoCompleteProps {
  value: Territoire | null;
  onChange?: (territoire: Territoire | null) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

function TerritoireAutoComplete({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  disabled = false,
}: Readonly<TerritoireAutoCompleteProps>): ReactNode {
  const [territoires, setTerritoires] = useState<Array<Territoire>>([]);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getTerritoires(): Promise<void> {
      const _territoires = await territoiresService.getAll();
      _territoires.sort(sortingUtil.sortTerritoires);
      setTerritoires(_territoires);
    }

    void catchErrors(getTerritoires);
  }, [catchErrors]);

  return (
    <Autocomplete
      value={territoires.find((c) => c.id === value?.id) ?? value ?? null}
      onChange={(_e, _value: Territoire | null) => {
        onChange?.(_value);
      }}
      options={territoires}
      filterOptions={filterOptions}
      fullWidth
      autoHighlight
      openOnFocus
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      size={size}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={({ libelle }) => libelle}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
      getOptionKey={({ id }) => id}
      noOptionsText={isLoading ? <Skeleton /> : "Pas de résultat"}
      disabled={disabled}
    />
  );
}

export const TerritoireAutoCompleteWithErrorBoundary =
  withComponentErrorBoundary(TerritoireAutoComplete);
